import Avatar from '-/Custom/Avatar';
import type { ReactNode } from 'react';
import Animated, { FadeInDown, FadeOutDown } from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import PressableRow from '@/components/UI/PressableRow';
import Box from '@/components/Unistyles/Box';
import Text from '@/components/Unistyles/Text';
import { graphql, type ResultOf } from '@/gql/graphql';
import getAvatarText from '@/utils/getAvatarText';

const MENTION_SHEET_ENTERING = FadeInDown.duration(150);
const MENTION_SHEET_EXITING = FadeOutDown.duration(100);
const keepEditorFocused:
  | { onMouseDown: (event: { preventDefault(): void }) => void }
  | undefined =
  process.env.EXPO_OS === 'web'
    ? { onMouseDown: (event) => event.preventDefault() }
    : undefined;

export const ChatMentionSuggestionFragment = graphql(/* GraphQL */ `
  fragment ChatMentionSuggestion on person {
    id
    name
    avatar_id
    color
  }
`);

type ChatMentionSuggestionsProps = {
  people: readonly ResultOf<typeof ChatMentionSuggestionFragment>[];
  selectedPersonId: string | null;
  onSelect: (person: ResultOf<typeof ChatMentionSuggestionFragment>) => void;
};

function MentionSuggestionList({ children }: { children: ReactNode }) {
  return (
    <Box
      accessibilityLabel="Mention suggestions"
      // @ts-expect-error RN omits listbox from Role; RN Web forwards it to the DOM.
      role={process.env.EXPO_OS === 'web' ? 'listbox' : 'list'}
      nativeID="ChatMentionSuggestions"
    >
      {children}
    </Box>
  );
}

export default function ChatMentionSuggestions({
  people,
  selectedPersonId,
  onSelect,
}: ChatMentionSuggestionsProps) {
  return (
    <Animated.View
      entering={MENTION_SHEET_ENTERING}
      exiting={MENTION_SHEET_EXITING}
      style={styles.mentionSheet}
    >
      <Box style={styles.mentionSheetContent} testID="ChatMentionSuggestions">
        <MentionSuggestionList>
          {people.map((person) => {
            const isSelected = person.id === selectedPersonId;

            return (
              <PressableRow
                accessibilityLabel={`Mention ${person.name ?? 'person'}`}
                accessibilityState={{ selected: isSelected }}
                aria-selected={isSelected}
                key={person.id}
                {...keepEditorFocused}
                nativeID={`ChatMentionSuggestionOption-${person.id}`}
                onPress={() => onSelect(person)}
                role="option"
                tabIndex={-1}
                style={[
                  styles.suggestion,
                  isSelected ? styles.selectedSuggestion : undefined,
                ]}
                testID={`ChatMentionSuggestion-${person.id}`}
              >
                <Avatar
                  id={person.avatar_id ?? ''}
                  color={person.color}
                  name={getAvatarText(person.name ?? '')}
                  size="sm"
                />
                <Text numberOfLines={1} size="md">
                  {person.name}
                </Text>
              </PressableRow>
            );
          })}
        </MentionSuggestionList>
      </Box>
    </Animated.View>
  );
}

const styles = StyleSheet.create((theme) => ({
  mentionSheet: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    paddingHorizontal: theme.space('md'),
    paddingBottom: theme.space('sm'),
    width: '100%',
    zIndex: 1000,
  },
  mentionSheetContent: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    elevation: 8,
    overflow: 'hidden',
    shadowColor: theme.colors.black,
    shadowOffset: {
      height: theme.size(2),
      width: 0,
    },
    shadowOpacity: 0.3,
    shadowRadius: theme.size(4),
    _web: {
      boxShadow: `0 ${theme.size(2)}px ${theme.size(4)}px rgba(0, 0, 0, 0.3)`,
    },
  },
  suggestion: {
    minHeight: theme.size(14),
  },
  selectedSuggestion: {
    backgroundColor: theme.colors.surfaceSecondary,
  },
}));
